(() => {
  const messages = host => JSON.parse(host.querySelector('[data-alert-messages]').textContent);
  async function post(path, body) {
    const response = await fetch(path, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      credentials: 'same-origin', body: JSON.stringify(body), signal: AbortSignal.timeout(15000),
    });
    const result = await response.json();
    if (!response.ok) throw Object.assign(new Error(result.error), { code: result.error });
    return result;
  }
  class StockAlert extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.owner = this.closest('product-info');
      this.openButton = this.querySelector('[data-alert-open]');
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-alert-status]');
      this.cancelLink = this.querySelector('[data-alert-cancel]');
      this.labels = messages(this);
      this.variantId = this.dataset.variantId;
      this.available = this.dataset.available === 'true';
      this.openButton.addEventListener('click', () => {
        if (!this.variantId || this.available) return;
        this.dialog.showModal();
      }, options);
      this.querySelector('[data-alert-close]').addEventListener('click', () => this.dialog.close(), options);
      this.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); }, options);
      this.dialog.addEventListener('close', () => this.openButton.focus(), options);
      // Broadcast's updateVariantInputs dispatches change after updating the variant-selects HTML.
      this.owner?.addEventListener('change', event => {
        if (event.target.matches('input[name="id"]')) {
          const raw = this.owner.querySelector('variant-selects [data-selected-variant]')?.textContent;
          this.sync(raw ? JSON.parse(raw) : null);
        }
      }, options);
      if (typeof window.subscribe === 'function') {
        this.unsubscribe?.();
        this.unsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) === this.owner?.dataset.section) this.sync(data.variant);
        });
      }
      this.sync({ id: this.variantId, available: this.available });
    }
    disconnectedCallback() { this.abort?.abort(); this.unsubscribe?.(); }
    sync(variant) {
      const previous = this.variantId;
      this.variantId = variant?.id ? String(variant.id) : '';
      this.available = variant?.available !== false;
      this.openButton.disabled = !this.variantId || this.available || !!this.busy;
      this.hidden = !this.variantId || this.available;
      if (variant?.title) this.querySelector('[data-alert-variant]').textContent = variant.title === 'Default Title' ? '' : variant.title;
      if (previous !== this.variantId) { this.status.textContent = ''; this.form.hidden = false; if (this.cancelLink) { this.cancelLink.hidden = true; this.cancelLink.removeAttribute('href'); } this.dialog.close(); }
    }
    async submit() {
      if (this.busy || !this.variantId || this.available) return;
      const emailInput = this.form.elements.email;
      if (emailInput.required && !this.form.reportValidity()) return;
      const variantId = this.variantId;
      this.busy = true;
      this.form.querySelector('button[type=submit]').disabled = true;
      try {
        const result = await post(`${this.dataset.proxy.replace(/\/$/, '')}/subscribe`, {
          productId: this.dataset.productId, variantId, locale: this.dataset.locale, country: this.dataset.country,
          email: emailInput.value, website: this.form.elements.website.value,
          consent: true, consentVersion: this.dataset.consentVersion,
          newsletterConsent: this.form.elements.newsletter.checked,
          marketingConsentVersion: this.dataset.marketingConsentVersion, newsletterConsentVersion: this.dataset.newsletterConsentVersion,
        });
        if (variantId === this.variantId) {
          this.status.textContent = this.labels[result.status] || this.labels.error; this.form.hidden = true;
          if (this.cancelLink) {
            this.cancelLink.hidden = !result.unsubscribeToken;
            if (result.unsubscribeToken) this.cancelLink.href = `${this.dataset.proxy.replace(/\/$/, '')}/action#action=unsubscribe&token=${encodeURIComponent(result.unsubscribeToken)}`;
            else this.cancelLink.removeAttribute('href');
          }
        }
      } catch (error) {
        if (variantId !== this.variantId) return;
        if (error.code === 'email_required' || error.code === 'invalid_email') {
          this.dataset.customer = 'false'; this.querySelector('[data-alert-email]').hidden = false;
          emailInput.required = true; emailInput.focus();
        }
        this.status.textContent = this.labels[error.code] || this.labels.error;
      } finally {
        this.busy = false; this.form.querySelector('button[type=submit]').disabled = false;
        this.sync({ id: this.variantId, available: this.available });
      }
    }
  }
  class StockAlertAction extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const labels = messages(this), form = this.querySelector('form'), status = this.querySelector('[data-alert-status]');
      const params = new URLSearchParams(location.hash.slice(1)), token = params.get('token'), action = params.get('action');
      // Fragments never reach the server. Remove the token from the address bar once read.
      if (token) history.replaceState(null, '', location.pathname + location.search);
      const isToken = token && ['confirm', 'unsubscribe', 'privacy-export', 'privacy-delete'].includes(action);
      if (isToken) { this.querySelector('[data-action-request]').hidden = true; form.elements.email.required = false; }
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = form.querySelector('button'); if (button.disabled) return; button.disabled = true;
        try {
          const base = location.pathname.replace(/\/action\/?$/, '');
          const path = isToken ? action.replace('privacy-', 'privacy/') : 'privacy/request';
          const result = await post(`${base}/${path}`, isToken ? { token } : { email: form.elements.email.value, action: form.elements.action.value, locale: this.dataset.locale });
          if (action === 'privacy-export' && isToken) {
            const url = URL.createObjectURL(new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' }));
            const link = document.createElement('a'); link.href = url; link.download = 'stock-alert-data.json'; link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000); status.textContent = labels.exported;
          } else status.textContent = labels[result.status] || labels.error;
          form.hidden = true;
        } catch (error) { status.textContent = labels[error.code] || labels.error; button.disabled = false; }
      });
    }
  }
  if (!customElements.get('stock-alert')) customElements.define('stock-alert', StockAlert);
  if (!customElements.get('stock-alert-action')) customElements.define('stock-alert-action', StockAlertAction);
})();
